import { computed } from "vue";

import type { CardProps } from "./useCardData";

export function usePricing(
  props: CardProps,
  prices: any,
  isStoreAllProductsPage: any
) {
  const shouldHideLabPricing = computed(() => {
    return props.result?.type === "lab" && !isStoreAllProductsPage.value;
  });

  const findLabProductId = (
    productIds: any,
    globalProducts: any,
    currentPrice: any
  ) => {
    // Try product_ids array format
    if (Array.isArray(productIds) && productIds.length > 0) {
      return productIds.find((id: string) =>
        currentPrice?.find((item: any) => item.productGuid === id)
      );
    }

    // Try product_ids.raw format
    if (productIds?.raw?.length > 0) {
      return productIds.raw.find((id: string) =>
        currentPrice?.find((item: any) => item.productGuid === id)
      );
    }

    // Try global_products format
    if (globalProducts?.length > 0) {
      for (const product of globalProducts) {
        const productId = product?.global_product_id;
        if (
          productId &&
          currentPrice?.find((item: any) => item.productGuid === productId)
        ) {
          return productId;
        }
      }
    }

    return null;
  };

  const priceDetails = computed(() => {
    if (!isStoreAllProductsPage.value && props.result?.type !== "lab") {
      return {};
    }

    const currentPrice = prices.value ?? [];

    if (props.result?.type === "lab") {
      const productIds = (props.result as any)?.product_ids;
      const globalProducts = (props.result as any)?.global_products;
      const labProductId = findLabProductId(
        productIds,
        globalProducts,
        currentPrice
      );

      if (labProductId) {
        return (
          currentPrice?.find(
            (item: any) => item.productGuid === labProductId
          ) ?? {}
        );
      }
      return {};
    }

    return (
      currentPrice?.find((item: any) => item.guid === props.result.guid) ?? {}
    );
  });

  const price = computed((): string | null => {
    if (shouldHideLabPricing.value) return null;

    const productPrice =
      priceDetails.value?.discount_price > 0
        ? priceDetails.value.discount_price
        : priceDetails.value.price;
    return productPrice ? productPrice.toString() : null;
  });

  const listprice = computed((): string | null => {
    if (shouldHideLabPricing.value || props.priceView !== "USD") return null;

    return priceDetails.value?.discount_price > 0
      ? priceDetails.value?.price
      : null;
  });

  const clcPrice = computed((): string | null => {
    if (shouldHideLabPricing.value) return null;

    if (priceDetails.value?.clc_count > 0) {
      return priceDetails.value.clc_count.toString();
    }
    return null;
  });

  const displayedUsdPrice = computed((): string | null => {
    return props.priceView === "USD" ? price.value : null;
  });

  const displayedClcPrice = computed((): string | null => {
    return props.priceView === "CLC" ? clcPrice.value : null;
  });

  const usdOrClcCurrency = computed((): string => {
    if (props.priceView === "USD" && displayedUsdPrice.value) return "USD";
    if (props.priceView === "CLC" && displayedClcPrice.value) return "CLCs";
    return "";
  });

  const discountPercentage = (
    price: number,
    discount_price: number
  ): string | null => {
    if (discount_price == 0 || discount_price > price) return null;

    const discount = price - discount_price;
    const percentageDiscount = Math.trunc((discount / price) * 100);
    return `${percentageDiscount}%`;
  };

  const salePercentage = computed(() => {
    if (shouldHideLabPricing.value) return null;

    if (priceDetails.value?.discount_price > 0) {
      const { price, discount_price } = priceDetails.value;
      return discountPercentage(price, discount_price);
    }
    return null;
  });

  return {
    priceDetails,
    price,
    listprice,
    clcPrice,
    displayedUsdPrice,
    displayedClcPrice,
    usdOrClcCurrency,
    salePercentage,
    discountPercentage,
  };
}
